/**
 * Checkout Diagnostic Script
 * Compares cart state vs checkout state to diagnose discount issues
 */

(function() {
  'use strict';
  
  debugLog('🔍 Checkout Diagnostic Script Loaded');
  
  // Store cart state before checkout
  let cartStateBeforeCheckout = null;
  let checkoutState = null;
  
  // Function to diagnose cart vs checkout discrepancy
  function diagnoseCheckoutIssue() {
    debugLog('\n═══════════════════════════════════════════════════════════');
    debugLog('🔍 CHECKOUT DIAGNOSTIC: Starting Comprehensive Analysis');
    debugLog('═══════════════════════════════════════════════════════════');
    debugLog('Timestamp:', new Date().toISOString());
    debugLog('Current URL:', window.location.href);
    debugLog('Current pathname:', window.location.pathname);
    
    const activeDiscount = localStorage.getItem('active_discount');
    debugLog('\n--- LOCAL STORAGE ---');
    debugLog('Active discount from localStorage:', activeDiscount);
    debugLog('All localStorage keys:', Object.keys(localStorage));
    
    // Check cookies too
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      acc[key] = value;
      return acc;
    }, {});
    debugLog('Cookies:', cookies);
    
    if (!activeDiscount) {
      debugLog('⚠️ No active discount found in localStorage');
      debugLog('This might be why checkout doesn\'t show discount!');
      return;
    }
    
    debugLog('\n--- FETCHING CART STATE ---');
    debugLog('Fetching /cart.js...');
    
    // Fetch current cart state
    fetch('/cart.js')
      .then(response => {
        debugLog('Cart API response status:', response.status);
        debugLog('Cart API response headers:', Object.fromEntries(response.headers.entries()));
        return response.json();
      })
      .then(cart => {
        debugLog('\n--- RAW CART RESPONSE ---');
        debugLog('Full cart object:', JSON.stringify(cart, null, 2));
        debugLog('\n═══════════════════════════════════════════════════════════');
        debugLog('📦 CART STATE ANALYSIS');
        debugLog('═══════════════════════════════════════════════════════════');
        
        debugLog('\n--- CART TOTALS ---');
        debugLog('Cart total_price (cents):', cart.total_price);
        debugLog('Cart total_price (formatted):', '$' + (cart.total_price / 100).toFixed(2));
        
        const cartOriginalTotal = cart.items.reduce((sum, item) => sum + item.original_line_price, 0);
        debugLog('Cart original_total (calculated, cents):', cartOriginalTotal);
        debugLog('Cart original_total (formatted):', '$' + (cartOriginalTotal / 100).toFixed(2));
        
        const discountAmount = cartOriginalTotal - cart.total_price;
        debugLog('Discount amount (cents):', discountAmount);
        debugLog('Discount amount (formatted):', '$' + (discountAmount / 100).toFixed(2));
        debugLog('Discount percentage:', ((discountAmount / cartOriginalTotal) * 100).toFixed(2) + '%');
        
        debugLog('\n--- DISCOUNT CODES ---');
        debugLog('cart.discount_codes:', cart.discount_codes);
        debugLog('cart.discount_codes type:', typeof cart.discount_codes);
        debugLog('cart.discount_codes length:', cart.discount_codes?.length || 0);
        
        if (cart.discount_codes && cart.discount_codes.length > 0) {
          cart.discount_codes.forEach((code, index) => {
            debugLog(`  Discount code ${index + 1}:`, {
              code: code.code,
              type: typeof code.code,
              full_object: code
            });
          });
        } else {
          debugLog('  ⚠️ NO DISCOUNT CODES IN CART!');
        }
        
        debugLog('\n--- CART LEVEL DISCOUNT APPLICATIONS ---');
        debugLog('cart.cart_level_discount_applications:', cart.cart_level_discount_applications);
        debugLog('cart.cart_level_discount_applications type:', typeof cart.cart_level_discount_applications);
        debugLog('cart.cart_level_discount_applications length:', cart.cart_level_discount_applications?.length || 0);
        
        if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
          cart.cart_level_discount_applications.forEach((app, index) => {
            debugLog(`  Application ${index + 1}:`, {
              title: app.title,
              value: app.value,
              value_type: app.value_type,
              total_allocated_amount: app.total_allocated_amount,
              full_object: app
            });
          });
        } else {
          debugLog('  ⚠️ NO CART LEVEL DISCOUNT APPLICATIONS!');
        }
        
        debugLog('\n--- CART ITEMS DETAIL ---');
        debugLog('Total items:', cart.items.length);
        cart.items.forEach((item, index) => {
          debugLog(`\n  Item ${index + 1}:`, {
            id: item.id,
            variant_id: item.variant_id,
            product_id: item.product_id,
            title: item.product_title,
            variant_title: item.variant_title,
            quantity: item.quantity,
            price: item.price,
            price_formatted: '$' + (item.price / 100).toFixed(2),
            original_line_price: item.original_line_price,
            original_line_price_formatted: '$' + (item.original_line_price / 100).toFixed(2),
            final_line_price: item.final_line_price,
            final_line_price_formatted: '$' + (item.final_line_price / 100).toFixed(2),
            line_level_discount_allocations: item.line_level_discount_allocations,
            properties: item.properties,
            sku: item.sku
          });
          
          if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
            debugLog(`    Line-level discounts for item ${index + 1}:`, item.line_level_discount_allocations);
          }
        });
        
        cartStateBeforeCheckout = cart;
        
        // Check if discount code is in cart
        const discountInCart = cart.discount_codes && cart.discount_codes.length > 0 && 
          cart.discount_codes.some(code => {
            const codeMatch = code.code === activeDiscount || 
                             code.code?.toUpperCase() === activeDiscount.toUpperCase() ||
                             (typeof code === 'string' && code === activeDiscount);
            debugLog(`  Comparing: "${code.code || code}" === "${activeDiscount}"`, codeMatch);
            return codeMatch;
          });
        
        debugLog('\n--- DISCOUNT CODE MATCHING ---');
        debugLog('Looking for discount code:', activeDiscount);
        debugLog('Discount code type:', typeof activeDiscount);
        debugLog('Discount code length:', activeDiscount?.length);
        debugLog('Discount code in cart.discount_codes?', discountInCart);
        
        // Detailed matching
        if (cart.discount_codes && cart.discount_codes.length > 0) {
          debugLog('  Checking each code:');
          cart.discount_codes.forEach((code, index) => {
            const codeValue = typeof code === 'string' ? code : code.code;
            const exactMatch = codeValue === activeDiscount;
            const caseInsensitiveMatch = codeValue?.toUpperCase() === activeDiscount.toUpperCase();
            debugLog(`    Code ${index + 1}: "${codeValue}"`);
            debugLog(`      Exact match: ${exactMatch}`);
            debugLog(`      Case-insensitive match: ${caseInsensitiveMatch}`);
            debugLog(`      Type: ${typeof codeValue}`);
          });
        } else {
          debugLog('  ⚠️ cart.discount_codes is empty or null!');
        }
        
        // Check URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const discountInUrl = urlParams.get('discount');
        debugLog('\n--- URL PARAMETERS ---');
        debugLog('Current URL:', window.location.href);
        debugLog('URL search params:', window.location.search);
        debugLog('Discount in URL?', discountInUrl);
        debugLog('Discount in URL matches localStorage?', discountInUrl === activeDiscount);
        
        // Check if discount is applied but not in discount_codes
        const hasDiscountApplication = cart.cart_level_discount_applications && 
          cart.cart_level_discount_applications.length > 0;
        const hasPriceDifference = discountAmount > 0;
        
        debugLog('\n--- DISCOUNT APPLICATION STATUS ---');
        debugLog('Has discount applications?', hasDiscountApplication);
        debugLog('Has price difference?', hasPriceDifference);
        debugLog('Discount amount > 0?', discountAmount > 0);
        
        if (hasDiscountApplication && !discountInCart) {
          debugLog('  ⚠️ WARNING: Discount is applied but NOT in discount_codes!');
          debugLog('  This might cause checkout to not show the discount!');
        }
        
        // Predict what checkout should show
        debugLog('\n═══════════════════════════════════════════════════════════');
        debugLog('🔮 CHECKOUT PREDICTION');
        debugLog('═══════════════════════════════════════════════════════════');
        
        if (discountInCart) {
          debugLog('✅ Discount IS in cart.discount_codes');
          debugLog('✅ Checkout SHOULD show discount');
          debugLog('Expected checkout total (cents):', cart.total_price);
          debugLog('Expected checkout total (formatted):', '$' + (cart.total_price / 100).toFixed(2));
        } else if (hasDiscountApplication) {
          debugLog('⚠️ Discount is APPLIED but NOT in discount_codes');
          debugLog('⚠️ Checkout MIGHT show discount (depends on Shopify)');
          debugLog('Cart total (cents):', cart.total_price);
          debugLog('Cart total (formatted):', '$' + (cart.total_price / 100).toFixed(2));
          debugLog('Original total (formatted):', '$' + (cartOriginalTotal / 100).toFixed(2));
        } else {
          debugLog('❌ Discount NOT in cart.discount_codes');
          debugLog('❌ Discount NOT applied');
          debugLog('❌ Checkout will NOT show discount');
          debugLog('Cart total (cents):', cart.total_price);
          debugLog('Cart total (formatted):', '$' + (cart.total_price / 100).toFixed(2));
          debugLog('Expected checkout total (without discount, cents):', cartOriginalTotal);
          debugLog('Expected checkout total (without discount, formatted):', '$' + (cartOriginalTotal / 100).toFixed(2));
        }
        
        // Store for comparison
        window.__cartStateBeforeCheckout = cart;
        window.__activeDiscount = activeDiscount;
        window.__diagnosticTimestamp = new Date().toISOString();
        
        debugLog('\n═══════════════════════════════════════════════════════════');
        debugLog('✅ CHECKOUT DIAGNOSTIC: Analysis Complete');
        debugLog('═══════════════════════════════════════════════════════════');
        debugLog('Cart state stored in window.__cartStateBeforeCheckout');
        debugLog('Active discount stored in window.__activeDiscount');
        debugLog('Timestamp stored in window.__diagnosticTimestamp');
        debugLog('\n💡 Next steps:');
        debugLog('  1. Click checkout button');
        debugLog('  2. On checkout page, run: window.compareCartVsCheckout()');
        debugLog('  3. Check console for mismatch detection');
      })
      .catch(error => {
        debugError('❌ Error diagnosing cart state:', error);
      });
  }
  
  // Run diagnosis when checkout button is clicked
  document.addEventListener('click', function(event) {
    const checkoutButton = event.target.closest('#checkout, [name="checkout"], button[name="checkout"]');
    if (checkoutButton) {
      debugLog('\n🔍 Checkout button clicked - Running diagnostic...');
      diagnoseCheckoutIssue();
    }
  });
  
  // Also run on page load if we're on checkout page
  if (window.location.pathname.includes('/checkout')) {
    debugLog('🔍 On checkout page - Running diagnostic...');
    
    // Wait a bit for checkout to load
    setTimeout(() => {
      diagnoseCheckoutIssue();
      
      // Try to get checkout state (if accessible)
      const checkoutTotalElement = document.querySelector('[data-checkout-total], .order-summary__section--total, .total-line__price');
      if (checkoutTotalElement) {
        const checkoutTotal = checkoutTotalElement.textContent.trim();
        debugLog('\n--- CHECKOUT STATE (After Load) ---');
        debugLog('Checkout total displayed:', checkoutTotal);
        
        if (window.__cartStateBeforeCheckout) {
          const cartTotal = window.__cartStateBeforeCheckout.total_price;
          const cartFormatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(cartTotal / 100);
          
          debugLog('\n--- COMPARISON ---');
          debugLog('Cart total (before checkout):', cartFormatted);
          debugLog('Checkout total (displayed):', checkoutTotal);
          
          if (checkoutTotal !== cartFormatted) {
            debugError('❌ MISMATCH DETECTED!');
            debugError('Cart total:', cartFormatted);
            debugError('Checkout total:', checkoutTotal);
            debugError('Difference detected - discount may not have carried over');
          } else {
            debugLog('✅ Totals match - discount carried over correctly');
          }
        }
      }
    }, 2000);
  }
  
  // Function to compare cart vs checkout
  function compareCartVsCheckout() {
    debugLog('\n═══════════════════════════════════════════════════════════');
    debugLog('🔍 COMPARING CART VS CHECKOUT');
    debugLog('═══════════════════════════════════════════════════════════');
    debugLog('Timestamp:', new Date().toISOString());
    debugLog('Current URL:', window.location.href);
    
    if (!window.__cartStateBeforeCheckout) {
      debugLog('⚠️ No cart state stored. Run diagnosis before checkout.');
      debugLog('💡 Run window.diagnoseCheckout() first');
      return;
    }
    
    const cartState = window.__cartStateBeforeCheckout;
    const activeDiscount = window.__activeDiscount;
    const diagnosticTimestamp = window.__diagnosticTimestamp;
    
    debugLog('\n--- STORED DATA ---');
    debugLog('Cart state timestamp:', diagnosticTimestamp);
    debugLog('Active discount:', activeDiscount);
    debugLog('Cart total from stored state (cents):', cartState.total_price);
    debugLog('Cart total from stored state (formatted):', '$' + (cartState.total_price / 100).toFixed(2));
    
    // Try multiple selectors for checkout total
    debugLog('\n--- SEARCHING FOR CHECKOUT TOTAL ---');
    const checkoutTotalSelectors = [
      '[data-checkout-total]',
      '.order-summary__section--total .total-line__price',
      '.total-line__price',
      '[data-total-price]',
      '.total-line--final .total-line__price',
      '.order-summary__section--total .total-line--final .total-line__price',
      '.order-summary__section--total .order-summary__section__content .total-line__price',
      '[data-order-summary-section="total"] .total-line__price',
      '.order-summary__section .total-line__price',
      '.order-summary .total-line__price'
    ];
    
    let checkoutTotalElement = null;
    let foundSelector = null;
    
    checkoutTotalSelectors.forEach(selector => {
      const element = document.querySelector(selector);
      if (element && !checkoutTotalElement) {
        checkoutTotalElement = element;
        foundSelector = selector;
        debugLog(`  ✅ Found element with selector: ${selector}`);
        debugLog(`     Element text: "${element.textContent.trim()}"`);
        debugLog(`     Element HTML: ${element.outerHTML.substring(0, 200)}`);
      }
    });
    
    if (!checkoutTotalElement) {
      debugLog('  ⚠️ Could not find checkout total element with any selector');
      debugLog('  Available elements on page:');
      const allPriceElements = document.querySelectorAll('[class*="price"], [class*="total"], [data*="price"], [data*="total"]');
      debugLog(`  Found ${allPriceElements.length} potential price/total elements`);
      allPriceElements.forEach((el, index) => {
        if (index < 10) { // Limit to first 10
          debugLog(`    ${index + 1}. ${el.tagName}.${el.className} = "${el.textContent.trim().substring(0, 50)}"`);
        }
      });
      debugLog('\n  Cart state before checkout:', cartState);
      return;
    }
    
    const checkoutTotalText = checkoutTotalElement.textContent.trim();
    debugLog('\n--- PARSING CHECKOUT TOTAL ---');
    debugLog('Raw checkout total text:', checkoutTotalText);
    
    // Try multiple parsing methods
    const parseMethods = [
      () => checkoutTotalText.match(/[\d,]+\.?\d*/)?.[0],
      () => checkoutTotalText.match(/\$?([\d,]+\.?\d*)/)?.[1],
      () => checkoutTotalText.match(/([\d,]+\.?\d*)/)?.[1]
    ];
    
    let checkoutTotal = null;
    let parsedValue = null;
    
    parseMethods.forEach((method, index) => {
      const match = method();
      if (match && !parsedValue) {
        parsedValue = match.replace(/,/g, '');
        checkoutTotal = parseFloat(parsedValue) * 100;
        debugLog(`  Method ${index + 1} parsed: ${parsedValue} = ${checkoutTotal} cents`);
      }
    });
    
    if (!checkoutTotal) {
      debugLog('  ⚠️ Could not parse checkout total');
      debugLog('  Checkout total text:', checkoutTotalText);
    }
    
    debugLog('\n═══════════════════════════════════════════════════════════');
    debugLog('📊 COMPARISON RESULTS');
    debugLog('═══════════════════════════════════════════════════════════');
    debugLog('Cart total (before checkout, cents):', cartState.total_price);
    debugLog('Cart total (before checkout, formatted):', '$' + (cartState.total_price / 100).toFixed(2));
    debugLog('Checkout total (displayed, cents):', checkoutTotal || 'N/A');
    debugLog('Checkout total (displayed, formatted):', checkoutTotal ? '$' + (checkoutTotal / 100).toFixed(2) : checkoutTotalText);
    
    if (checkoutTotal) {
      const difference = Math.abs(checkoutTotal - cartState.total_price);
      const differenceFormatted = '$' + (difference / 100).toFixed(2);
      
      debugLog('\n--- DIFFERENCE ANALYSIS ---');
      debugLog('Difference (cents):', difference);
      debugLog('Difference (formatted):', differenceFormatted);
      debugLog('Difference threshold (1 cent):', difference > 1);
      
      if (difference > 1) {
        debugError('\n❌❌❌ MISMATCH DETECTED! ❌❌❌');
        debugError('Cart total:', '$' + (cartState.total_price / 100).toFixed(2));
        debugError('Checkout total:', '$' + (checkoutTotal / 100).toFixed(2));
        debugError('Difference:', differenceFormatted);
        debugError('Discount may NOT have carried over to checkout!');
        
        // Check discount code status
        debugLog('\n--- DISCOUNT CODE STATUS ---');
        debugLog('Active discount:', activeDiscount);
        const discountInCart = cartState.discount_codes?.some(code => {
          const codeValue = typeof code === 'string' ? code : code.code;
          return codeValue === activeDiscount || codeValue?.toUpperCase() === activeDiscount.toUpperCase();
        });
        debugLog('Discount in cart.discount_codes?', discountInCart);
        debugLog('Cart discount_codes:', cartState.discount_codes);
        
        if (!discountInCart) {
          debugError('  ⚠️ DISCOUNT CODE NOT IN cart.discount_codes!');
          debugError('  This is likely why checkout doesn\'t show the discount!');
        }
        
        // Check if discount field exists in checkout
        debugLog('\n--- CHECKOUT DISCOUNT FIELD ---');
        const discountInputs = [
          document.querySelector('input[name="discount"]'),
          document.querySelector('input[name="discount_code"]'),
          document.querySelector('#discount-code'),
          document.querySelector('[name="discount"]'),
          document.querySelector('[name="discount_code"]')
        ].filter(el => el !== null);
        
        if (discountInputs.length > 0) {
          discountInputs.forEach((input, index) => {
            debugLog(`  Discount input ${index + 1} found:`, {
              name: input.name,
              id: input.id,
              value: input.value,
              placeholder: input.placeholder,
              element: input
            });
          });
        } else {
          debugLog('  ⚠️ No discount input field found in checkout');
          debugLog('  Searching for discount-related elements...');
          const discountElements = document.querySelectorAll('[class*="discount"], [id*="discount"], [data*="discount"]');
          debugLog(`  Found ${discountElements.length} discount-related elements`);
          discountElements.forEach((el, index) => {
            if (index < 5) {
              debugLog(`    ${index + 1}. ${el.tagName}.${el.className} = "${el.textContent.trim().substring(0, 50)}"`);
            }
          });
        }
        
        // Check URL for discount parameter
        const urlParams = new URLSearchParams(window.location.search);
        const discountInUrl = urlParams.get('discount');
        debugLog('\n--- URL DISCOUNT PARAMETER ---');
        debugLog('Discount in URL?', discountInUrl);
        debugLog('Discount in URL matches localStorage?', discountInUrl === activeDiscount);
        
      } else {
        debugLog('\n✅✅✅ TOTALS MATCH! ✅✅✅');
        debugLog('Discount carried over correctly to checkout!');
      }
    } else {
      debugLog('\n⚠️ Could not parse checkout total for comparison');
    }
    
    debugLog('\n═══════════════════════════════════════════════════════════');
  }
  
  // Expose diagnostic functions globally
  window.diagnoseCheckout = diagnoseCheckoutIssue;
  window.compareCartVsCheckout = compareCartVsCheckout;
  debugLog('💡 Run window.diagnoseCheckout() to diagnose cart state');
  debugLog('💡 Run window.compareCartVsCheckout() to compare cart vs checkout');
})();
